import { useEffect, useState } from "react";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { KnowledgeBase } from "@/api/types";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useDeleteKb,
  useUpdateKb,
} from "@/features/knowledge-bases/hooks/useKbs";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  kb: KnowledgeBase;
}

export function SettingsTab({ kb }: Props) {
  const update = useUpdateKb(kb.id);
  const remove = useDeleteKb();
  const canManage = useCan(Action.KNOWLEDGE_BASE_UPDATED, {
    owner_team_id: kb.owner_team_id,
  });
  const canDelete = useCan(Action.KNOWLEDGE_BASE_DELETED, {
    owner_team_id: kb.owner_team_id,
  });

  const [name, setName] = useState(kb.name);
  const [shortDescription, setShortDescription] = useState(
    kb.short_description ?? "",
  );
  const [description, setDescription] = useState(kb.description ?? "");
  const [chunkSize, setChunkSize] = useState(kb.chunk_size);
  const [chunkOverlap, setChunkOverlap] = useState(kb.chunk_overlap);
  const [isActive, setIsActive] = useState(kb.is_active);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    setName(kb.name);
    setShortDescription(kb.short_description ?? "");
    setDescription(kb.description ?? "");
    setChunkSize(kb.chunk_size);
    setChunkOverlap(kb.chunk_overlap);
    setIsActive(kb.is_active);
  }, [kb]);

  const dirty =
    name !== kb.name ||
    shortDescription !== (kb.short_description ?? "") ||
    description !== (kb.description ?? "") ||
    chunkSize !== kb.chunk_size ||
    chunkOverlap !== kb.chunk_overlap ||
    isActive !== kb.is_active;

  const overlapInvalid = chunkOverlap >= chunkSize;

  async function handleSave() {
    if (overlapInvalid) {
      toast.error("chunk_overlap deve ser menor que chunk_size");
      return;
    }
    try {
      await update.mutateAsync({
        name,
        short_description: shortDescription || undefined,
        description: description || undefined,
        chunk_size: chunkSize,
        chunk_overlap: chunkOverlap,
        is_active: isActive,
      });
      toast.success("Base atualizada");
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete() {
    try {
      await remove.mutateAsync(kb.id);
      toast.success("Base desativada");
      setConfirmDeleteOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  function handleDiscard() {
    setName(kb.name);
    setShortDescription(kb.short_description ?? "");
    setDescription(kb.description ?? "");
    setChunkSize(kb.chunk_size);
    setChunkOverlap(kb.chunk_overlap);
    setIsActive(kb.is_active);
  }

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="kb-set-name">Nome</Label>
        <Input
          id="kb-set-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canManage}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="kb-set-short-desc">Descrição curta</Label>
        <Textarea
          id="kb-set-short-desc"
          rows={2}
          maxLength={280}
          value={shortDescription}
          onChange={(e) => setShortDescription(e.target.value)}
          disabled={!canManage}
        />
        <p className="text-xs text-muted-foreground">
          Aparece na lista de bases e no topo desta base.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="kb-set-desc">Descrição longa</Label>
        <Textarea
          id="kb-set-desc"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!canManage}
        />
        <p className="text-xs text-muted-foreground">
          Detalhes da base. É o contexto que os agentes recebem sobre ela.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="kb-set-size">Tamanho do chunk</Label>
          <Input
            id="kb-set-size"
            type="number"
            min={100}
            max={5000}
            value={chunkSize}
            onChange={(e) => setChunkSize(Number(e.target.value))}
            disabled={!canManage}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="kb-set-overlap">Overlap</Label>
          <Input
            id="kb-set-overlap"
            type="number"
            min={0}
            max={500}
            value={chunkOverlap}
            onChange={(e) => setChunkOverlap(Number(e.target.value))}
            disabled={!canManage}
          />
        </div>
      </div>
      {overlapInvalid ? (
        <p className="text-sm text-destructive">
          chunk_overlap deve ser menor que chunk_size.
        </p>
      ) : null}
      <div className="flex items-center gap-2">
        <input
          id="kb-set-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          disabled={!canManage}
        />
        <Label htmlFor="kb-set-active">Base ativa</Label>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={handleSave}
          disabled={
            !canManage || !dirty || overlapInvalid || update.isPending
          }
        >
          Salvar
        </Button>
        <Button
          variant="outline"
          onClick={handleDiscard}
          disabled={!canManage || !dirty || update.isPending}
        >
          Descartar
        </Button>
      </div>
      {canDelete ? (
        <div className="space-y-2 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-danger-700">
            Zona de risco
          </h3>
          <Button
            variant="destructive"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={remove.isPending || !kb.is_active}
          >
            Desativar base
          </Button>
        </div>
      ) : null}

      <ConfirmDestructiveDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Desativar base de conhecimento"
        description="Esta ação desativa a base e ela deixa de aparecer em buscas semânticas. Os documentos permanecem armazenados."
        resourceName={kb.name}
        resourceKind="base"
        confirmLabel="Desativar base"
        onConfirm={handleDelete}
        isPending={remove.isPending}
      />
    </div>
  );
}
